import { getDatabase, getEncryptionKey } from '../index';
import { mergeDiffs } from '../sync/diffUtils';
import { encrypt, decrypt } from '../encryption';

/**
 * Encrypt a pending operation's sensitive payload (`data` for creates, `diff`
 * for updates) into an `_encrypted` blob, leaving the routing metadata
 * (entityType, entityId, operationType, caseId, createdAt, baseRevision)
 * plaintext so the sync engine can still index/group operations.
 *
 * The crypto runs here in plain JS — NOT in the DBCore encryption middleware —
 * because the queue's read-modify-write merge can't hold an IndexedDB
 * transaction open across an async `crypto.subtle` call. Returns the object
 * unchanged when no key is set (pre-auth / tests) or there is nothing sensitive
 * to encrypt (e.g. delete ops).
 */
async function toStored(op) {
  const key = getEncryptionKey();
  if (!key) return op;

  const sensitive = {};
  if (op.data != null) sensitive.data = op.data;
  if (op.diff != null) sensitive.diff = op.diff;
  if (Object.keys(sensitive).length === 0) return op;

  const { data, diff, ...rest } = op;
  return { ...rest, _encrypted: await encrypt(key, sensitive) };
}

/** Reverse of toStored: restore `data`/`diff` from the `_encrypted` blob. */
async function fromStored(row) {
  if (!row) return row;
  const key = getEncryptionKey();
  // Legacy rows written before this change have no `_encrypted` and are already
  // plaintext — pass them through so the queue keeps draining after an upgrade.
  if (!key || !row._encrypted) return row;

  const { _encrypted, ...rest } = row;
  return { ...rest, ...(await decrypt(key, _encrypted)) };
}

// Exported for the legacy migration, which bulk-inserts pending ops directly
// and must encrypt their payloads the same way before writing.
export { toStored as encryptPendingOp };

/**
 * PendingRepository
 * 
 * Manages the queue of pending operations waiting to be synced to the server.
 * Operations are stored as diffs (only changed fields) for efficiency.
 * 
 * When syncing:
 * - Multiple updates to same entity are merged into single diff
 * - Create + updates merged into single create
 * - Create + delete = remove from queue (never synced)
 * - Update + delete = just delete
 */

/**
 * Operation types
 */
export const OperationType = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete'
};

/**
 * Entity types
 */
export const EntityType = {
  CASE: 'case',
  JUROR: 'juror'
};

/**
 * Add a pending create operation
 * @param {string} entityType - 'case' or 'juror'
 * @param {string} entityId - Entity ID (local UUID)
 * @param {Object} data - Full entity data
 * @param {string} [caseId] - Parent case ID (for jurors)
 */
export async function addCreate(entityType, entityId, data, caseId = null) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  await db.pendingOperations.add(await toStored({
    entityType,
    entityId,
    operationType: OperationType.CREATE,
    caseId,
    data,
    baseRevision: 0,
    createdAt: new Date().toISOString()
  }));

  console.log(`Pending create added: ${entityType} ${entityId}`);
}

/**
 * Add a pending update operation (stores only the diff)
 * @param {string} entityType - 'case' or 'juror'
 * @param {string} entityId - Entity ID
 * @param {Object} diff - Only the changed fields
 * @param {number} baseRevision - Revision this update is based on
 * @param {string} [caseId] - Parent case ID (for jurors)
 */
export async function addUpdate(entityType, entityId, diff, baseRevision, caseId = null) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  // Check if there's already a pending operation for this entity
  const existing = await fromStored(await db.pendingOperations
    .where({ entityType, entityId })
    .first());

  if (existing) {
    if (existing.operationType === OperationType.CREATE) {
      // Merge update into the create data. Use put (not update) on the row we
      // already read: Table.update opens one transaction for its get+put, and
      // re-reading the encrypted row in the merge would break it.
      await db.pendingOperations.put(await toStored({
        ...existing,
        data: { ...existing.data, ...diff }
      }));
      console.log(`Pending update merged into create: ${entityType} ${entityId}`);
      return;
    }

    if (existing.operationType === OperationType.UPDATE) {
      // Merge diffs (put, not update — see note above).
      const mergedDiff = mergeDiffs([existing.diff, diff]);
      await db.pendingOperations.put(await toStored({
        ...existing,
        diff: mergedDiff
      }));
      console.log(`Pending updates merged: ${entityType} ${entityId}`);
      return;
    }
    
    if (existing.operationType === OperationType.DELETE) {
      // Update after delete? Shouldn't happen, ignore
      console.warn(`Ignoring update for deleted entity: ${entityType} ${entityId}`);
      return;
    }
  }

  // No existing operation, add new update
  await db.pendingOperations.add(await toStored({
    entityType,
    entityId,
    operationType: OperationType.UPDATE,
    caseId,
    diff,
    baseRevision,
    createdAt: new Date().toISOString()
  }));

  console.log(`Pending update added: ${entityType} ${entityId}`);
}

/**
 * Add a pending delete operation
 * @param {string} entityType - 'case' or 'juror'
 * @param {string} entityId - Entity ID
 * @param {string} [caseId] - Parent case ID (for jurors)
 */
export async function addDelete(entityType, entityId, caseId = null) {
  const db = getDatabase();
  if (!db) throw new Error('Database not initialized');

  // Check if there's already a pending operation for this entity. Decrypt it so
  // the replacement below carries no stale `_encrypted` blob.
  const existing = await fromStored(await db.pendingOperations
    .where({ entityType, entityId })
    .first());

  if (existing) {
    if (existing.operationType === OperationType.CREATE) {
      // Delete a locally-created entity that was never synced
      // Just remove from pending queue
      await db.pendingOperations.delete(existing.id);
      console.log(`Pending create cancelled by delete: ${entityType} ${entityId}`);
      return;
    }

    // Replace any existing operation with delete. A delete carries no sensitive
    // payload, so this row is stored plaintext (toStored is a no-op for nulls).
    await db.pendingOperations.put(await toStored({
      ...existing,
      operationType: OperationType.DELETE,
      diff: null,
      data: null
    }));
    console.log(`Pending operation replaced with delete: ${entityType} ${entityId}`);
    return;
  }

  // No existing operation, add new delete
  await db.pendingOperations.add({
    entityType,
    entityId,
    operationType: OperationType.DELETE,
    caseId,
    data: null,
    diff: null,
    baseRevision: null,
    createdAt: new Date().toISOString()
  });

  console.log(`Pending delete added: ${entityType} ${entityId}`);
}

/**
 * Get all pending operations
 * @returns {Promise<Array>}
 */
export async function getAll() {
  const db = getDatabase();
  if (!db) return [];

  const rows = await db.pendingOperations.orderBy('createdAt').toArray();
  return Promise.all(rows.map(fromStored));
}

/**
 * Get pending operations for a specific case (including its jurors)
 * @param {string} caseId - Case ID
 * @returns {Promise<Array>}
 */
export async function getForCase(caseId) {
  const db = getDatabase();
  if (!db) return [];

  // Get case operations and juror operations for this case
  const caseOps = await db.pendingOperations
    .where({ entityType: EntityType.CASE, entityId: caseId })
    .toArray();

  const jurorOps = await db.pendingOperations
    .where({ caseId })
    .toArray();

  const all = await Promise.all([...caseOps, ...jurorOps].map(fromStored));
  return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

/**
 * Get pending operations grouped by case for batch sync
 * @returns {Promise<Object>} - { caseId: { case: op, jurors: { created, updated, deleted } } }
 */
export async function getGroupedForSync() {
  const db = getDatabase();
  if (!db) return {};

  const allOps = await getAll();
  const grouped = {};

  for (const op of allOps) {
    const caseId = op.entityType === EntityType.CASE ? op.entityId : op.caseId;
    
    if (!grouped[caseId]) {
      grouped[caseId] = {
        case: null,
        jurors: {
          created: [],
          updated: [],
          deleted: []
        }
      };
    }

    if (op.entityType === EntityType.CASE) {
      grouped[caseId].case = op;
    } else {
      switch (op.operationType) {
        case OperationType.CREATE:
          grouped[caseId].jurors.created.push(op);
          break;
        case OperationType.UPDATE:
          grouped[caseId].jurors.updated.push(op);
          break;
        case OperationType.DELETE:
          grouped[caseId].jurors.deleted.push(op);
          break;
      }
    }
  }

  return grouped;
}

/**
 * Get count of pending operations
 * @returns {Promise<number>}
 */
export async function getCount() {
  const db = getDatabase();
  if (!db) return 0;

  return db.pendingOperations.count();
}

/**
 * Check if there are any pending operations
 * @returns {Promise<boolean>}
 */
export async function hasPending() {
  return (await getCount()) > 0;
}

/**
 * Remove a pending operation by ID
 * @param {number} id - Operation ID
 */
export async function remove(id) {
  const db = getDatabase();
  if (!db) return;

  await db.pendingOperations.delete(id);
}

/**
 * Remove multiple pending operations by IDs
 * @param {number[]} ids - Operation IDs
 */
export async function removeMany(ids) {
  const db = getDatabase();
  if (!db) return;

  await db.pendingOperations.bulkDelete(ids);
}

/**
 * Clear all pending operations (use with caution!)
 */
export async function clearAll() {
  const db = getDatabase();
  if (!db) return;

  await db.pendingOperations.clear();
  console.log('All pending operations cleared');
}

/**
 * Update multiple entity IDs in bulk after server assigns real IDs
 * @param {string} entityType - Entity type
 * @param {Array<{localId, serverId}>} mappings - Array of ID mappings
 */
export async function bulkUpdateEntityIds(entityType, mappings) {
  const db = getDatabase();
  if (!db || mappings.length === 0) return;

  const localIds = mappings.map(m => m.localId);
  const serverIdMap = new Map(mappings.map(m => [m.localId, m.serverId]));

  // Find all pending operations that reference any of these local IDs
  const ops = await db.pendingOperations
    .where('entityId').anyOf(localIds)
    .and(op => op.entityType === entityType)
    .toArray();

  if (ops.length > 0) {
    const updatedOps = ops.map(op => {
      const serverId = serverIdMap.get(op.entityId);
      return serverId ? { ...op, entityId: serverId } : op;
    });
    await db.pendingOperations.bulkPut(updatedOps);
  }

  // Also update caseId references for juror operations if we're mapping case IDs
  if (entityType === EntityType.CASE) {
    const jurorOps = await db.pendingOperations
      .where('caseId').anyOf(localIds)
      .toArray();

    if (jurorOps.length > 0) {
      const updatedJurorOps = jurorOps.map(op => {
        const serverId = serverIdMap.get(op.caseId);
        return serverId ? { ...op, caseId: serverId } : op;
      });
      await db.pendingOperations.bulkPut(updatedJurorOps);
    }
  }
}

/**
 * Update entity ID after server assigns real ID (for creates)
 * @param {string} entityType - Entity type
 * @param {string} localId - Local UUID
 * @param {string} serverId - Server-assigned ID
 */
export async function updateEntityId(entityType, localId, serverId) {
  const db = getDatabase();
  if (!db) return;

  // Update any pending operations that reference this entity. Use put on the
  // rows we already read (not update): the encryption middleware awaits crypto
  // between a get and its put, which would close Table.update's transaction.
  const ops = await db.pendingOperations
    .where({ entityType, entityId: localId })
    .toArray();

  for (const op of ops) {
    await db.pendingOperations.put({ ...op, entityId: serverId });
  }

  // Also update caseId references for juror operations
  if (entityType === EntityType.CASE) {
    const jurorOps = await db.pendingOperations
      .where({ caseId: localId })
      .toArray();

    for (const op of jurorOps) {
      await db.pendingOperations.put({ ...op, caseId: serverId });
    }
  }
}

export default {
  OperationType,
  EntityType,
  addCreate,
  addUpdate,
  addDelete,
  getAll,
  getForCase,
  getGroupedForSync,
  getCount,
  hasPending,
  remove,
  removeMany,
  clearAll,
  bulkUpdateEntityIds,
  updateEntityId
};
